/**
 * Only for PuduMT1 page, page location pages/products/mt1.page.tsx
 */

import { useTranslations } from 'next-intl'
import { Key, useMemo, useState } from 'react'
import { useChinaDetector } from '../common/region-locale'
import { AspectRatio, IProductParamsPanelProps, IVerticalSingleMedia, Theme } from '../common/pd-meta-components-types'
import { useMobileDetector } from './use-mobile-detector'

type Section1Type = {
  bgiUri: string
  bgClassName?: string
  bgStyle?: { [key in string]: Key }
  contentClassName?: string
  contentStyle?: { [key in string]: Key }
  titleUri: string
  titleClassName?: string
  titleStyle?: { [key in string]: Key }
  slogan: string
  videoUrl: string
}

type Section2Type = {
  title: string[]
  gradientBG: boolean
  mediaStyle?: { [key in string]: Key }
  mediaClassName?: string
  mediaUri: string
}

type Section3Type = {
  dataSource: {
    mediaUri: string
    mediaClassName?: string
    mediaStyle?: { [key in string]: Key }
    title: string[]
    desc: string[]
    infoGroup?: {
      data: string
      desc: string
    }[]
  }[]
}

type Section4Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
}

type Section5Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
  gradientBG: boolean
}

type Section6Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
}

type Section7Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
  gradientBG: boolean
}

type Section8Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
}

type Section9Type = {
  title: string[]
  data: {
    mediaUri: string
    mediaClassName?: string
    mediaStyle?: { [key in string]: Key }
    reverseCondition?: boolean
    title: string[]
    infoGroup?: {
      data: string
      desc: string
    }[]
  }[]
}

type Section10Type = {
  title: string[]
  desc: string[]
  mediaUri: string
  mediaClassName?: string
  mediaStyle?: { [key in string]: Key }
  rowMediaList: {
    mediaUri: string
    mediaClassName?: string
    mediaStyle?: { [key in string]: Key }
  }[]
  rowMediaDesc: string[]
}

type Section11Type = {
  dataSource: {
    mediaUri: string
    mediaClassName?: string
    mediaStyle?: { [key in string]: Key }
    reverseCondition?: boolean
    title: string[]
    titleSize?: string
    desc: string[]
    infoGroup: {
      data: string
      desc: string
    }[]
  }[]
}

type Section12Type = {
  title: string[]
  data: {
    title: string
    mediaUri: string
  }[]
}

type Section13Type = {
  title: string[]
  desc: string[]
  descInline?: boolean
  data: {
    mediaUri: string
    mediaClassName?: string
    mediaStyle?: { [key in string]: Key }
  }[]
}

type Section14Type = {
  paramsList: {
    gap: string
    label: string
    value: string[]
  }[]
  imgUrl: string
}

type AutoEmptyStationParam = {
  label: string
  value: string[]
}

export function usePuduMT1() {
  const t = useTranslations()
  const inChina = useChinaDetector()
  const isMobileDevice = useMobileDetector()
  const section1Data: Section1Type = useMemo(
    () => ({
      bgiUri: isMobileDevice
        ? 'https://cdn.pudutech.com/official-website/mt1/S1-1-h5.webp'
        : 'https://cdn.pudutech.com/official-website/mt1/S1-1.webp',
      bgStyle: isMobileDevice ? { height: '177.86vw' } : { height: '56.25vw' },
      contentStyle: isMobileDevice ? { top: '16vw' } : { top: '5.9vw' },
      titleUri: 'https://cdn.pudutech.com/official-website/mt1/name.svg',
      titleStyle: isMobileDevice ? { height: '7.626vw', width: '56.26vw' } : { height: '2.97vw', width: '21.98vw' },
      slogan: t('Section1.slogan'),
      videoUrl: inChina
        ? 'https://cdn.pudutech.com/official-website/mt1/video_mt1_zh.mp4'
        : 'https://cdn.pudutech.com/official-website/mt1/video_mt1_en.mp4',
    }),
    [isMobileDevice, inChina]
  )

  const section2Data: Section2Type = useMemo(
    () => ({
      title: [t('Section2.title1')],
      gradientBG: true,
      mediaStyle: isMobileDevice ? { height: '177.86vw' } : { height: '56.25vw' },
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S2-1.mp4',
    }),
    [isMobileDevice]
  )

  const section3Data: Section3Type = useMemo(
    () => ({
      dataSource: [
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S3-1.mp4',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : undefined,
          title: [t('Section3.title1')],
          desc: [t('Section3.desc1')],
          reverseCondition: isMobileDevice ? false : false,
          infoGroup: [
            { data: '35L', desc: t('Section3.infoGroup1.desc1') },
            { data: '70cm', desc: t('Section3.infoGroup1.desc2') },
          ],
        },
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S3-2.mp4',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : undefined,
          title: [t('Section3.title2')],
          desc: [t('Section3.desc2')],
          reverseCondition: isMobileDevice ? false : true,
        },
      ],
    }),
    [isMobileDevice]
  )

  const section4Data: Section4Type = useMemo(
    () => ({
      title: [t('Section4.title1')],
      desc: [t('Section4.desc1')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S4-1.mp4',
      mediaClassName: 'laptop:h-150',
      mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
    }),
    [isMobileDevice]
  )

  const section5Data: Section5Type = useMemo(
    () => ({
      title: [t('Section5.title1')],
      desc: [t('Section5.desc1')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S5-1.webp',
      mediaStyle: isMobileDevice ? { height: '177.86vw' } : { height: '56.25vw' },
      gradientBG: true,
    }),
    [isMobileDevice]
  )

  const section6Data: Section6Type = useMemo(
    () => ({
      title: [t('Section6.title1')],
      desc: [t('Section6.desc2')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S6-1.mp4',
      mediaClassName: 'laptop:h-150',
      mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
    }),
    [isMobileDevice]
  )

  const section7Data: Section7Type = useMemo(
    () => ({
      title: [t('Section7.title1')],
      desc: [t('Section7.desc1')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S7-1.mp4',
      mediaStyle: isMobileDevice ? { height: '177.86vw' } : { height: '56.25vw' },
      gradientBG: true,
    }),
    [isMobileDevice]
  )

  const section8Data: Section8Type = useMemo(
    () => ({
      title: [t('Section8.title1')],
      desc: [t('Section8.desc1')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S7-2.webp',
      mediaClassName: 'laptop:h-150',
      mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
    }),
    [isMobileDevice]
  )

  const section9Data: Section9Type = useMemo(
    () => ({
      title: [t('Section9.title1')],
      data: [
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S8-1.mp4',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : undefined,
          title: [t('Section9.data1.title1')],
          reverseCondition: isMobileDevice ? true : false,
          infoGroup: [{ data: '75cm', desc: t('Section9.data1.desc1') }],
        },
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S8-3.mp4',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : undefined,
          title: [t('Section9.data3.title1')],
          reverseCondition: true,
        },
      ],
    }),
    [isMobileDevice]
  )

  const autoEmptyStationData: IVerticalSingleMedia = {
    theme: Theme.Dark,
    title: `${t('AutoEmptyStation.title')}[1]`,
    titleClassName: 'laptop:leading-[56px]',
    titleSupClassName:
      'top-0 font-normal text-fc-secondary text-[12px] leading-[18px] laptop:text-[18px] laptop:leading-[26px]',
    backgroundClassName: 'bg-anti-blk',
    desc: t('AutoEmptyStation.desc'),
    mediaGroup: [{ mediaUrl: 'https://cdn.pudutech.com/Auto_dumping_Station_1_b1aa966d1c.webp' }],
    mediaAspectRatio: AspectRatio.TwoToOne,
  }

  const autoEmptyStationParamsData: IProductParamsPanelProps = {
    theme: Theme.Dark,
    title: t('parameterData.titleText'),
    backgroundClassName: 'bg-black',
    imgUrl: 'https://cdn.pudutech.com/Auto_dumping_Station_2_65c3d0c98c.webp',
    paramsList: (t.raw('AutoEmptyStation.params') as AutoEmptyStationParam[]).map((item) => ({
      ...item,
      gap: '',
    })),
    robot: 'mt1',
  }

  const autoEmptyStationNoteData = [`[1] ${t('AutoEmptyStation.note')}`]

  const section10Data: Section10Type = useMemo(
    () => ({
      title: [t('Section10.title1')],
      desc: [t('Section10.desc1'), t('Section10.desc2')],
      mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S9-1.webp',
      mediaClassName: ' laptop:h-150',
      mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
      rowMediaList: [
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S9-2.webp',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
        },
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S9-3.webp',
          mediaClassName: 'w-full  laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '66.66vw' } : undefined,
        },
      ],
      rowMediaDesc: [t('Section10.rowMediaDesc1'), t('Section10.rowMediaDesc2')],
    }),
    [isMobileDevice]
  )

  const section11Data: Section11Type = useMemo(
    () => ({
      dataSource: [
        {
          reverseCondition: isMobileDevice,
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S10-1.webp',
          mediaClassName: 'w-full laptop:w-588px laptop:h-110',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : undefined,
          title: [t('Section11.title1')],
          titleSize: 'text-24 laptop:text-48',
          desc: [t('Section11.desc1')],
          infoGroup: [
            { data: '8H', desc: t('Section11.infoGroup.desc1') },
            { data: '3H', desc: t('Section11.infoGroup.desc2') },
          ],
        },
      ],
    }),
    [isMobileDevice]
  )

  const section12Data: Section12Type = useMemo(
    () => ({
      title: [t('Section12.title1')],
      data: [
        {
          title: t('Section12.data1.title1'),
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S11-1.webp',
        },
        {
          title: t('Section12.data2.title1'),
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S11-2.webp',
        },
        {
          title: t('Section12.data3.title1'),
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/' + (inChina ? 'S11-3-cn.webp' : 'S11-3-en.webp'),
        },
      ],
    }),
    [isMobileDevice]
  )

  const section13Data: Section13Type = useMemo(
    () => ({
      title: [t('Section13.title1')],
      desc: [t('Section13.desc1'), t('Section13.desc2'), t('Section13.desc3')],
      descInline: !isMobileDevice,
      data: [
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S12-1.webp',
          mediaClassName: 'w-full',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : { height: '37.5vw' },
        },
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S12-2.webp',
          mediaClassName: 'w-full',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : { height: '37.5vw' },
        },
        {
          mediaUri: 'https://cdn.pudutech.com/official-website/mt1/S12-3.webp',
          mediaClassName: 'w-full',
          mediaStyle: isMobileDevice ? { height: '95.46vw' } : { height: '37.5vw' },
        },
      ],
    }),
    [isMobileDevice]
  )

  const section14Data: Section14Type = useMemo(
    () => ({
      paramsList: [
        {
          gap: '',
          label: t('Section14.params.label1'),
          value: inChina
            ? [t('Section14.params.value1')]
            : [t('Section14.params.value1'), t('Section14.params.value1_1')],
        },
        {
          gap: '',
          label: t('Section14.params.label2'),
          value: [t('Section14.params.value2')],
        },
        {
          gap: '',
          label: t('Section14.params.label3'),
          value: inChina
            ? [t('Section14.params.value3'), t('Section14.params.value3_1')]
            : [
                t('Section14.params.value3'),
                t('Section14.params.value3_1'),
                t('Section14.params.value3_2'),
                t('Section14.params.value3_3'),
              ],
        },
        {
          gap: '',
          label: t('Section14.params.label4'),
          value: [t('Section14.params.value4')],
        },
        {
          gap: '',
          label: t('Section14.params.label5'),
          value: [t('Section14.params.value5')],
        },
        {
          gap: '',
          label: t('Section14.params.label6'),
          value: [t('Section14.params.value6')],
        },
        {
          gap: '',
          label: t('Section14.params.label7'),
          value: [t('Section14.params.value7')],
        },
        {
          gap: '',
          label: t('Section14.params.label8'),
          value: inChina
            ? [t('Section14.params.value8')]
            : [t('Section14.params.value8'), t('Section14.params.value8_1')],
        },
      ],
      imgUrl: 'https://cdn.pudutech.com/official-website/mt1/S13-1.webp',
    }),
    [inChina]
  )
  return {
    section1Data,
    section2Data,
    section3Data,
    section4Data,
    section5Data,
    section6Data,
    section7Data,
    section8Data,
    section9Data,
    autoEmptyStationData,
    section10Data,
    section11Data,
    section12Data,
    section13Data,
    section14Data,
    autoEmptyStationParamsData,
    autoEmptyStationNoteData,
  }
}
